'use client';

import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ChevronRight, Info, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ResourceNeeds } from 'components/IAP/hooks/workAssignmentHooks';
import {
  PlannedResource,
  useUpdateWorkAssignmentListAttachment,
  useApproveWorkAssignmentListAttachment,
  useDenyWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from 'cn';
import ChangeLogDialog from './ChangeLogDialog';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import { useSectionChiefResolver } from './useSectionChiefResolver';

type ProposedStatusOption =
  | null
  | 'Draft'
  | 'Assigned'
  | 'Submitted Complete for Review'
  | 'Reported Incomplete for Review'
  | 'Completed'
  | 'Incomplete';

export type WorkGroupResourceNeed = ResourceNeeds & {
  workAssignmentId: string;
  workAssignmentName: string;
};

export type SimplifiedWorkAssignment = {
  id: string;
  type: string;
  name: string;
  description: string;
  workGroupName?: string;
  workGroupId?: string;
  overheadPosition?: string;
  specialInstructions?: string;
  reportingLocation?: string;
  timestamp?: string;
};

export type AssignmentListItem = {
  walaId: string | null;
  workGroupId: string;
  workGroup: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  proposedStatus: ProposedStatusOption;
  actualStatus: string | null;
  sectionChief?: string;
  sectionChiefContact?: string;
  branchDirector?: string;
  branchDirectorContact?: string;
  divisionGroupSupervisor?: string;
  divisionGroupSupervisorContact?: string;
  workAssignments: SimplifiedWorkAssignment[];
  workGroupResourceNeeds: WorkGroupResourceNeed[];
  plannedResources: PlannedResource[] | null;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
  timestamp?: Date;
};

export const createAssignmentListColumns = (
  periodNumber: number
): ColumnDef<AssignmentListItem>[] => [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => (
      <Button
        variant="ghost"
        size="icon-sm"
        className="tw:p-0"
        data-testid="assignment-list-row-expand-button"
        onClick={(e) => {
          e.stopPropagation();
          row.toggleExpanded();
        }}
        aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
      >
        <ChevronRight
          className={cn(
            'tw:h-4 tw:w-4 tw:transition-transform tw:duration-200',
            row.getIsExpanded() && 'tw:rotate-90'
          )}
        />
      </Button>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'workGroup',
    header: () => (
      <div className="tw:flex tw:items-center tw:space-x-2">
        <span>Assignee</span>
        <Tooltip>
          <TooltipTrigger>
            <Info size={16} />
          </TooltipTrigger>
          <TooltipContent>
            <p>
              Organizational unit for this 204. Once approved, the assigned user
              will be notified to complete it.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    ),
    cell: ({ row }) => (
      <span className="font-medium">{row.getValue('workGroup')}</span>
    ),
  },
  {
    accessorKey: 'proposedStatus',
    header: 'Status',
    cell: ({ row }) => (
      <div>
        <p className="tw:mb-0!">{row.getValue('proposedStatus')}</p>
        <p className="tw:mb-0!">0/3 Signatures</p>
      </div>
    ),
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => (
      <ActionsCell item={row.original} periodNumber={periodNumber} />
    ),
  },
];

function ActionsCell({
  item,
  periodNumber,
}: {
  item: AssignmentListItem;
  periodNumber: number;
}) {
  const [showChangeLog, setShowChangeLog] = useState(false);
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const { isCurrentUserSectionChief: isCurrentUserReviewer } =
    useSectionChiefResolver(item.workGroupId);
  const updateWala = useUpdateWorkAssignmentListAttachment();
  const approveWala = useApproveWorkAssignmentListAttachment();
  const denyWala = useDenyWorkAssignmentListAttachment();
  const { exportWorkAssignmentListAttachment } = useIcsFormPdfPreview();

  const assign = () => {
    if (!item.walaId) return;
    updateWala.mutate({ walaId: item.walaId, proposedStatus: 'in_progress' });
  };

  const setComplete = () => {
    if (!item.walaId) return;
    updateWala.mutate({ walaId: item.walaId, proposedStatus: 'completed' });
  };

  const setIncomplete = () => {
    if (!item.walaId) return;
    updateWala.mutate({ walaId: item.walaId, proposedStatus: 'incomplete' });
  };

  const approve = () => {
    if (!item.walaId) return;
    approveWala.mutate(item.walaId);
  };

  const deny = () => {
    if (!item.walaId) return;
    denyWala.mutate(item.walaId);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="tw:flex tw:items-center tw:justify-end tw:space-x-2!"
      onClick={(e) => e.stopPropagation()}
    >
      {item.proposedStatus === 'Assigned' && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button variant="outline" size="sm" disabled={!canEditCards} />
            }
          >
            Submit
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="dark">
            <DropdownMenuItem disabled={!canEditCards} onClick={setComplete}>
              Submit Completion
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!canEditCards} onClick={setIncomplete}>
              Report Inability to Complete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {item.actualStatus === 'awaiting_review' && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="outline"
                size="sm"
                disabled={!isCurrentUserReviewer}
              />
            }
          >
            Review
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="dark">
            <DropdownMenuItem
              disabled={!isCurrentUserReviewer}
              onClick={approve}
            >
              Mark Complete
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!isCurrentUserReviewer} onClick={deny}>
              Mark Incomplete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" className="tw:size-8" />}
        >
          <span className="sr-only">Open menu</span>
          <MoreVertical className="h-4 w-4" />
        </DropdownMenuTrigger>
        {/* rymi-TODO: Create ThemeProvider.tsx component instead of using dark class: https://ui.shadcn.com/docs/dark-mode/vite */}
        <DropdownMenuContent align="end" className="dark">
          <DropdownMenuItem
            disabled={!item.walaId || !canEditCards || !isCurrentUserReviewer}
            onClick={assign}
          >
            Assign to {item.workGroup}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!item.walaId}
            onClick={() => {
              exportWorkAssignmentListAttachment(
                item.walaId as string,
                periodNumber
              );
            }}
          >
            Preview
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!item.walaId}
            onClick={() => setShowChangeLog(true)}
          >
            Change Log
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            className="tw:hidden"
            disabled={!item.walaId}
            onClick={() => {}}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showChangeLog && item.walaId && (
        <ChangeLogDialog
          onClose={() => setShowChangeLog(false)}
          walaId={item.walaId}
        />
      )}
    </div>
  );
}
